import { AuthErrorKind } from '@griply/types';
import { getAuthErrorMessageKey } from '@griply/utils';
import Typography from '@mui/material/Typography';
import { useConfirmEmailChange, useSignInAgain } from 'features/user';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { AuthPageLayout } from 'shared/components/ui/auth-page-layout';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import { QueryParamOptions, useQueryParam } from 'shared/lib/react-router-dom';
import { Paths } from 'shared/routes/paths';

import * as Styled from './confirm-email-change.style';

// Confirms on a click, never on load: mail scanners open links on their own.
export const ConfirmEmailChange: React.FC = () => {
  const { t } = useTranslation();
  const [token] = useQueryParam(QueryParamOptions.EmailChangeToken);
  const { confirmEmailChange, isLoading, isSuccess, email, error } =
    useConfirmEmailChange();
  const signInAgain = useSignInAgain();

  const isDeadLink = !token || error === AuthErrorKind.InvalidLink;
  const isReady = !isSuccess && !isDeadLink;
  const errorMessageKey =
    error === AuthErrorKind.EmailTaken
      ? 'auth.change-email.errors.email-taken'
      : (error && getAuthErrorMessageKey(error)) || null;

  const onConfirm = () => confirmEmailChange(token ?? '');
  const onSignInAgain = () => {
    void signInAgain();
  };

  return (
    <AuthPageLayout>
      <Typography variant="h1" textAlign="center">
        {t(
          isSuccess
            ? 'auth.confirm-email-change.success.title'
            : 'auth.confirm-email-change.title',
        )}
      </Typography>

      <Styled.Content>
        {isSuccess && (
          <>
            <Typography variant="body2" textAlign="center">
              {t('auth.confirm-email-change.success.message', { email })}
            </Typography>
            <Button
              variant={Variants.Contained}
              color={Colors.Primary}
              size={Sizes.Medium}
              onClick={onSignInAgain}
            >
              {t('forms.reset-password.back-to-login.label')}
            </Button>
          </>
        )}

        {isDeadLink && (
          <>
            <Typography variant="body2" textAlign="center">
              {t('auth.errors.invalid-link')}
            </Typography>
            <Styled.Anchor to={Paths.Login}>
              {t('forms.reset-password.back-to-login.label')}
            </Styled.Anchor>
          </>
        )}

        {isReady && (
          <>
            <Typography variant="body2" textAlign="center">
              {t('auth.confirm-email-change.description')}
            </Typography>
            <Button
              variant={Variants.Contained}
              color={Colors.Primary}
              size={Sizes.Medium}
              onClick={onConfirm}
              disabled={isLoading}
              disabledBackgroundOnly={isLoading}
            >
              {isLoading ? (
                <Styled.Loader color="primary" size={14} />
              ) : (
                t('auth.confirm-email-change.submit')
              )}
            </Button>
            {!!errorMessageKey && (
              <Styled.ErrorMessage>{t(errorMessageKey)}</Styled.ErrorMessage>
            )}
          </>
        )}
      </Styled.Content>
    </AuthPageLayout>
  );
};
